import { Injectable } from '@angular/core';
import { Query } from '@syncfusion/ej2-data';
import { CheckBoxFilterBase } from '@syncfusion/ej2-grids';

/**
 * The `@syncfusion/ej2-grids` version whose `getAllData` / `processDataOperation` /
 * `dataSuccess` this shim was read against and verified on. The shim wraps a private method,
 * so it is a versioned adapter by design: the guard in
 * `syncfusion-grid-filter-scope.service.spec.ts` fails the suite when the installed version
 * differs, which forces the upgrader to re-read those three methods in
 * `checkbox-filter-base.js` (where the row cap is pushed, where `customQuery` is decided, and
 * where the other columns' filters are replayed) and bump this pin rather than letting the
 * popup's behaviour change silently.
 */
export const VERIFIED_EJ2_GRIDS_VERSION = '34.1.33';

/** One step of an ej2-data `Query`, as `Query.queries` holds it. */
interface QueryStep {
  fn?: string;
  e?: unknown;
}

/** The slice of `CheckBoxFilterBase` this shim reads and wraps. */
interface CheckBoxFilterInternals {
  options?: { isRemote?: boolean; filteredColumns?: unknown[] };
  /** Set by Syncfusion when it already put the other columns' filters on the query itself. */
  customQuery?: boolean;
  /** Syncfusion's own builder of the other columns' predicates (and the grid search). */
  queryGenerate(query: Query): void;
  processDataOperation(query: Query, isInitial?: boolean): void;
}

/**
 * Scopes a grid filter popup's value list by the other columns' filters BEFORE Syncfusion's
 * row cap, for grids bound to a local array.
 *
 * When an Excel / checkbox filter popup opens, Syncfusion builds its value list from a
 * query it caps at `filterChoiceCount` rows (1000 by default, `getAllData`) and only then,
 * on the rows that came back, replays the filters of the other columns (`dataSuccess` ->
 * `queryGenerate`). ej2-data runs query steps in the order they were pushed, so the cap takes
 * the first 1000 UNFILTERED rows and the replay runs on that slice. Once a column is
 * filtered to values that live beyond row 1000 — the Compliance Repository filtered to a
 * "Demo Act" whose rows sat at the end of a few thousand compliances — every other column's
 * popup replayed that filter over a slice that held none of its rows and rendered
 * "No matches found". The popup's search box did not suffer, because its refresh pushes its
 * own `where` before the cap.
 *
 * `processDataOperation` is the interception point: it receives the finished initial query
 * (cap already on it) right before it is executed. For the initial list of a local source,
 * when at least one column is filtered and Syncfusion has not already scoped the query itself
 * (`customQuery`, its infinite-scroll and no-distinct paths), the other columns' predicates
 * are generated with Syncfusion's own `queryGenerate` and placed ahead of every existing
 * step, and `customQuery` is set so `dataSuccess` does not replay them a second time. The
 * popup then lists the distinct values of the first 1000 rows that MATCH the other filters,
 * which is what the user expects after narrowing the grid. A search refresh, a remote source
 * and a query Syncfusion scoped itself are left exactly as they were.
 */
@Injectable({ providedIn: 'root' })
export class SyncfusionGridFilterScopeService {

  /** Guard so the prototype patch is installed exactly once per page load — static so even
   * a second service instance (e.g. in tests) cannot double-wrap the prototype. */
  private static installed = false;

  /**
   * Installs the filter-popup scope shim. Safe to call repeatedly — only the first call
   * patches the prototype.
   * @returns Nothing.
   */
  install(): void {
    if (SyncfusionGridFilterScopeService.installed) {
      return;
    }
    SyncfusionGridFilterScopeService.installed = true;
    this.patchProcessDataOperation();
  }

  /**
   * Wraps `processDataOperation` so the other columns' filters precede the row cap on a
   * popup's initial query (see the class description for the conditions).
   * @returns Nothing.
   */
  private patchProcessDataOperation(): void {
    // processDataOperation and queryGenerate are internal to the grid's filter base; the cast
    // is required to wrap them.
    const proto = CheckBoxFilterBase.prototype as unknown as CheckBoxFilterInternals;
    const originalProcessDataOperation = proto.processDataOperation;

    // Version guard: a Syncfusion upgrade that renames or drops either internal must degrade
    // to the unscoped list of before, never to a popup that throws.
    if (typeof originalProcessDataOperation !== 'function' || typeof proto.queryGenerate !== 'function') {
      return;
    }

    proto.processDataOperation = function (this: CheckBoxFilterInternals, query: Query, isInitial?: boolean): void {
      const steps = (query as unknown as { queries?: QueryStep[] } | undefined)?.queries;
      const filteredColumns = this.options?.filteredColumns;
      if (
        isInitial === true &&
        !this.customQuery &&
        !this.options?.isRemote &&
        Array.isArray(filteredColumns) && filteredColumns.length > 0 &&
        Array.isArray(steps)
      ) {
        // Generated on a fresh query so the predicates can be placed first; `queryGenerate`
        // leaves the open column's own filter out, so its popup still offers every value.
        const scoped = new Query();
        this.queryGenerate(scoped);
        const scopedSteps = (scoped as unknown as { queries: QueryStep[] }).queries;
        if (scopedSteps.length) {
          (query as unknown as { queries: QueryStep[] }).queries = [...scopedSteps, ...steps];
          this.customQuery = true;
        }
      }
      originalProcessDataOperation.call(this, query, isInitial);
    };
  }
}
